import { ref } from 'vue'
import { reportUpgradeRequired } from '../services/ApiService.js'

export function useCvDirectDownload() {
  const isDownloading = ref(false)
  const downloadError = ref(null)

  const getCSRFToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }

  const saveBlobFromResponse = async (response, fallbackFilename) => {
    const blob = await response.blob()
    const contentDisposition = response.headers.get('content-disposition') || ''
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : fallbackFilename

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)

    return filename
  }

  const fetchPdf = async (endpoint) => {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Accept': 'application/pdf',
        'X-CSRF-Token': getCSRFToken()
      }
    })

    if (response.status === 402) {
      // Pro feature — open the global UpgradeModal
      const data = await response.json().catch(() => ({}))
      reportUpgradeRequired(data)
      const error = new Error(data.message || 'Upgrade required')
      error.upgradeRequired = true
      throw error
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || `Failed to generate PDF (${response.status})`)
    }

    return response
  }

  // Download a saved CV by ID using server-side PDF generation
  const downloadCvDirectly = async (cvId) => {
    if (!cvId) {
      downloadError.value = 'CV ID is required'
      return { success: false, error: 'CV ID is required' }
    }

    isDownloading.value = true
    downloadError.value = null

    try {
      const response = await fetchPdf(`/api/cvs/${cvId}/pdf`)
      const filename = await saveBlobFromResponse(response, 'cv.pdf')
      return { success: true, filename }
    } catch (error) {
      console.error('PDF download error:', error)
      downloadError.value = error.message
      return { success: false, error: error.message }
    } finally {
      isDownloading.value = false
    }
  }

  // Download a saved CV with all reference documents merged into one PDF (Pro).
  // A 402 opens the global UpgradeModal and resolves with upgradeRequired: true.
  const downloadCvWithReferences = async (cvId) => {
    if (!cvId) {
      downloadError.value = 'CV ID is required'
      return { success: false, error: 'CV ID is required' }
    }

    isDownloading.value = true
    downloadError.value = null

    try {
      const response = await fetchPdf(`/api/cvs/${cvId}/pdf_with_references`)
      const filename = await saveBlobFromResponse(response, 'cv_with_references.pdf')
      return { success: true, filename }
    } catch (error) {
      if (error.upgradeRequired) {
        return { success: false, upgradeRequired: true, error: error.message }
      }
      console.error('PDF with references download error:', error)
      downloadError.value = error.message
      return { success: false, error: error.message }
    } finally {
      isDownloading.value = false
    }
  }

  return {
    isDownloading,
    downloadError,
    downloadCvDirectly,
    downloadCvWithReferences
  }
}
